import React from "react";
import {
    COURSE_END_CHOICE,
    DEFAULT_LISTING_AVAILABILITY_CHOICE,
} from "repoV2/features/Listing/Listing.constants";
import { IMerchandiseDetails } from "repoV2/features/Listing/Listing.interfaces";
import { PAYMENT_PLAN_TYPE } from "repoV2/constants/listing";
import { ICheckoutListingDetails } from "../utils/apis/ListingDetailsApi/IListingDetailsApi.interface";
import { ICheckoutCreatorDetails } from "../utils/apis/CreatorDetailsApi/ICreatorDetailsApi.interface";
import { checkout_page_button_types } from "../constants/CheckoutPages.constants";
import { merchandise_total_inventory } from "./PaymentDetailsSection/modules/FormGroups/modules/MerchandiseDetailsFormGroup/MerchandiseDetailsFormGroup.constants";

interface ICheckoutBasicDetailsContextValue {
    title: string; // Checkout Page title
    defaultListingTitle: string; // Listing Title, set by the creator at the time of listing creation
    listingUuid: string;
    listingSlug: string; // listing seo slug
    listingType: number; // listing type
    hostName: string; // host name, creator
    listingStatus: number; // numeric listing status - live, draft, waitlist, sold out
    coverImage: string; // cover image od listing
    subtitleText: string; // checkout page subtitle text (creator name default)
    subtitleIcon: string; // checkout page subtitle icon (creator icon default)
    description: string; // listing description using as checkout page description
    enableRecentSalesPopup: boolean; // check to enable recent sales popup
    hideCouponCodeField: boolean; // check to hide coupon field
    hideListingPrice: boolean; // check to hide listing price - will be express interest in this case
    isListingExpressInterest: boolean; // check to show express interest form instead of booking form to user
    isRenewSubscription: boolean; // check to show renew subscription form to user
    subscriptionDays: number | undefined; // total subscription days count if listing is of subscription type
    thankyouUuid: string; // thankyou page uuid, incase user is redirected to checkout page from thankyou page
    communityUuid: string; // community uuid, incase it is a bonus listing
    dynamicLinkUuid: string; // custom payment link uuid, incase it is a dynamic payment page
    paymentPlanType: number; // payment plan type - one time, subscription, part payment
    courseMagnitudeCount: number; // subscription magnitude count - total count of days or weeks or months
    courseMagnitudeChoice: number; // subscription choice - days or weeks or months
    customOrderBumpsHeading: string; // custom heading of the order bumps card shown to user
    enableRedirectionOnOrderBumps: boolean; // check to allow user to redirect to checkout page on order bump click
    hidePriceForZeroPriceListingOrderBumps: boolean; // check to hide the price part in order bump cards
    // incase this check is enabled on admin panel and the listing upsell price is zero
    isCourseValidityLifetime: boolean; // check if the lms has lifetime validity
    checkoutPageButtonType: number; // checkout page button type - 'checkout_page_button_types'
    checkoutPageButtonText: {
        mobileButtonText: string;
        paymentButtonText: string;
    };
    isCheckoutPagePreviewMode: boolean; // check if checkout page is open in preview mode or not
    creatorSubtitle: string;
    disableCheckEmailTopLevelDomain: boolean; // check if we need to validate the top level domain of the email user enters
    payoutGateway?: number | null; // payout gateway type PAYOUT_GATEWAY (repoV2/constants/payment.ts)
    availabilityChoice?: number;
    allowPostBookingSlotSelection?: boolean;
    enableMultipleUserCountFlow?: boolean;
    isShiprocketEnabled?: boolean;
    shippingDetails?: {
        shipping_charge: number; // shipping chargs
        shipping_days: number;
        shipping_charge_international: number; // international shipping charge
        shipping_days_international: number;
    };
    allowMerchandiseOverflow: boolean;
    purchasedMerchandiseInventory: number;
    totalMerchandiseInventory: number;
    termsAndConditionsUrl?: string;
    eInvoiceEnabled?: boolean; // refer ICreator.ICreatorDetails["einvoice_enabled"]
}

interface ICheckoutBasicDetailsContextProviderProps {
    listingDetails: ICheckoutListingDetails;
    creatorDetails: ICheckoutCreatorDetails;
    merchandiseDetails: IMerchandiseDetails;
    listingSlug: string;
    hostName: string;
    showExpressInterestForm: boolean;
    isRenewSubscription: boolean;
    thankyouUuid: string;
    communityUuid: string;
    dynamicLinkUuid: string;
    isCheckoutPagePreviewMode: boolean;
    children?: JSX.Element | JSX.Element[];
}

const initial_state: ICheckoutBasicDetailsContextValue = {
    title: "",
    defaultListingTitle: "",
    listingUuid: "",
    listingSlug: "",
    /* start */
    // @dev assuming default placeholder Value for these as Number.POSITIVE_INFINITY
    // (as its the maximum bigNumber available)
    // these values should always be filled but if not then this
    // will be used, as these values (listingType, listingStatus) cannot be bigger than this value
    // and thus this will be ignored in every switch selectors based on these values (listingType, listingStatus),
    listingType: Number.POSITIVE_INFINITY,
    listingStatus: Number.POSITIVE_INFINITY,
    /* end */
    hostName: "",
    coverImage: "",
    subtitleText: "",
    subtitleIcon: "",
    description: "",
    enableRecentSalesPopup: false,
    hideCouponCodeField: false,
    hideListingPrice: false,
    isListingExpressInterest: false,
    isRenewSubscription: false,
    subscriptionDays: undefined,
    thankyouUuid: "",
    communityUuid: "",
    dynamicLinkUuid: "",
    courseMagnitudeCount: 0,
    courseMagnitudeChoice: COURSE_END_CHOICE.DAYS,
    paymentPlanType: PAYMENT_PLAN_TYPE.ONE_TIME_PAYMENT,
    customOrderBumpsHeading: "",
    enableRedirectionOnOrderBumps: false,
    hidePriceForZeroPriceListingOrderBumps: false,
    isCourseValidityLifetime: false,
    checkoutPageButtonType: checkout_page_button_types.default,
    checkoutPageButtonText: {
        mobileButtonText: "",
        paymentButtonText: "",
    },
    creatorSubtitle: "",
    isCheckoutPagePreviewMode: false,
    disableCheckEmailTopLevelDomain: false,
    payoutGateway: null,
    availabilityChoice: DEFAULT_LISTING_AVAILABILITY_CHOICE,
    allowPostBookingSlotSelection: false,
    enableMultipleUserCountFlow: false,
    isShiprocketEnabled: false,
    shippingDetails: {
        shipping_charge: 0,
        shipping_charge_international: 0,
        shipping_days: 0,
        shipping_days_international: 0,
    },
    allowMerchandiseOverflow: false,
    purchasedMerchandiseInventory: 0,
    totalMerchandiseInventory: merchandise_total_inventory, // merchandise_total_inventory is the default value of total inventory
    termsAndConditionsUrl: "", // the url to redirect the user for the terms and conditions button
};

const CheckoutBasicDetailsContext = React.createContext(initial_state);

const CheckoutBasicDetailsContextProvider: React.FC<ICheckoutBasicDetailsContextProviderProps> =
    props => {
        const {
            listingDetails,
            creatorDetails,
            merchandiseDetails,
            listingSlug,
            hostName = "",
            showExpressInterestForm,
            isRenewSubscription,
            thankyouUuid,
            communityUuid,
            dynamicLinkUuid,
            isCheckoutPagePreviewMode,
            children,
        } = props;

        const {
            title = initial_state.title,
            default_listing_title:
                defaultListingTitle = initial_state.defaultListingTitle,
            cover_image: coverImage = initial_state.coverImage,
            description = initial_state.description,
            uuid: listingUuid = initial_state.listingUuid,
            type: listingType = initial_state.listingType,
            status: listingStatus = initial_state.listingStatus,
            payment_plan_type: paymentPlanType = initial_state.paymentPlanType,
            metadata = {},
            course_end_magnitude:
                courseMagnitudeCount = initial_state.courseMagnitudeCount,
            course_end_choice:
                courseMagnitudeChoice = initial_state.courseMagnitudeChoice,
            has_infinite_end: isCourseValidityLifetime,
            cta_details: buttonDetails = {},
            availability_choice:
                availabilityChoice = initial_state.availabilityChoice,
            allow_customer_scheduling_post_payment:
                allowPostBookingSlotSelection = initial_state.allowPostBookingSlotSelection,
        } = listingDetails;

        const {
            recent_sales_popup: showRecentSalesPopupForListing = false,
            hide_discount: hideCouponCodeField = false,
            subscription_days: subscriptionDays,
            hide_price: hideListingPrice = false,
            custom_order_bump_heading: customOrderBumpsHeading = "",
            enable_redirection_on_order_bump:
                enableRedirectionOnOrderBumps = false,
            enable_multi_user_flow:
                enableMultipleUserCountFlow = initial_state.enableMultipleUserCountFlow,
        } = metadata;

        const {
            button_type: buttonType = checkout_page_button_types.default,
            cta_data: ctaData = {},
        } = buttonDetails;

        const {
            mobile_button_text: mobileButtonText = "",
            payment_button_text: paymentButtonText = "",
        } = ctaData;

        const {
            creator_name: creatorName = "",
            display_image: creatorIcon = "",
            recent_sales_popup: showRecentSalesPopupForCreator = false,
            hide_price_for_zero_priced_order_bump_listing:
                hidePriceForZeroPriceListingOrderBumps = false,
            subtitle: creatorSubtitle = "",
            disable_booking_email_whitelisting:
                disableCheckEmailTopLevelDomain = false,
            payout_gateway: payoutGateway,
            custom_t_c:
                termsAndConditionsUrl = initial_state.termsAndConditionsUrl,
            einvoice_enabled: eInvoiceEnabled = false,
        } = creatorDetails;

        const {
            enable_shiprocket:
                isShiprocketEnabled = initial_state.isShiprocketEnabled,
            shipping_details: shippingDetails = initial_state.shippingDetails,
            allow_overflow:
                allowMerchandiseOverflow = initial_state.allowMerchandiseOverflow,
            purchased_inventory:
                purchasedMerchandiseInventory = initial_state.purchasedMerchandiseInventory,
            total_inventory:
                totalMerchandiseInventory = initial_state.totalMerchandiseInventory,
        } = merchandiseDetails || {};

        return (
            <CheckoutBasicDetailsContext.Provider
                value={{
                    title,
                    defaultListingTitle,
                    coverImage,
                    listingUuid,
                    hostName,
                    listingSlug,
                    listingType,
                    listingStatus,
                    subtitleText: creatorName,
                    subtitleIcon: creatorIcon,
                    description,
                    enableRecentSalesPopup:
                        showRecentSalesPopupForListing &&
                        showRecentSalesPopupForCreator,
                    hideCouponCodeField,
                    hideListingPrice,
                    isListingExpressInterest: showExpressInterestForm,
                    isRenewSubscription,
                    subscriptionDays,
                    thankyouUuid,
                    communityUuid,
                    dynamicLinkUuid,
                    courseMagnitudeCount,
                    courseMagnitudeChoice,
                    paymentPlanType,
                    customOrderBumpsHeading,
                    enableRedirectionOnOrderBumps,
                    hidePriceForZeroPriceListingOrderBumps,
                    isCourseValidityLifetime: Boolean(isCourseValidityLifetime),
                    checkoutPageButtonType: buttonType,
                    checkoutPageButtonText: {
                        mobileButtonText,
                        paymentButtonText,
                    },
                    // This value will exist only for dynamic payment pages
                    creatorSubtitle: dynamicLinkUuid ? creatorSubtitle : "",
                    isCheckoutPagePreviewMode,
                    disableCheckEmailTopLevelDomain,
                    payoutGateway,
                    availabilityChoice,
                    allowPostBookingSlotSelection,
                    enableMultipleUserCountFlow,
                    isShiprocketEnabled,
                    shippingDetails,
                    allowMerchandiseOverflow,
                    purchasedMerchandiseInventory,
                    totalMerchandiseInventory,
                    termsAndConditionsUrl,
                    eInvoiceEnabled,
                }}
            >
                {children}
            </CheckoutBasicDetailsContext.Provider>
        );
    };

export const useCheckoutBasicDetailsContext = () =>
    React.useContext(CheckoutBasicDetailsContext);

export default CheckoutBasicDetailsContextProvider;
